import { useState } from 'react'
import { Input } from './Input'
import { Button } from './Button'
import { Alert } from './Alert'
import { useTenantService, CreateManagedMerchantPayload, TenantResponse } from '../services/tenantService'

const companyNames = [
  'Harbour Conveyancing',
  'Bluegum Property Lawyers',
  'Cornerstone Conveyancing',
  'Oakbridge Legal',
  'Settlement Partners',
  'Summit Conveyancing',
  'Riverside Property Law',
  'Maple Street Conveyancing',
  'Northgate Legal',
  'Keyline Conveyancing',
  'Evergreen Property Lawyers',
  'Horizon Settlements',
  'Anchor Conveyancing',
  'Bridgepoint Legal',
  'First Title Conveyancing',
  'Crest Property Lawyers',
  'Beacon Conveyancing',
  'Stonegate Legal',
  'Prime Settlement Services',
  'Elm & Co Conveyancing',
  'Gateway Property Lawyers',
  'Lighthouse Conveyancing',
  'Clearwater Legal',
  'Parkview Conveyancing',
  'TrueNorth Property Law',
  'Pioneer Conveyancing',
  'Capital Settlement Group',
  'Acacia Legal',
  'Vista Conveyancing',
  'Heritage Property Lawyers',
  'Urban Title Services',
  'Silver Fern Conveyancing',
  'Ironwood Legal',
  'Blue Peak Conveyancing',
  'Coastal Property Lawyers',
  'Everstone Conveyancing',
  'Oakleaf Legal',
  'Precision Conveyancing',
  'Granite Property Law',
  'Landmark Conveyancing',
  'Civic Settlement Services',
  'Sterling Property Lawyers',
  'Westbrook Conveyancing',
  'Fairview Legal',
  'Foundation Conveyancing',
  'Hillside Property Lawyers',
  'Signature Conveyancing',
  'Trusted Title Group',
  'Cornerstone Settlement Services',
  'Metro Property Lawyers',
]

const firstNames = [
  'Olivia', 'Liam', 'Emma', 'Noah', 'Ava', 'William', 'Sophia', 'James',
  'Isabella', 'Benjamin', 'Mia', 'Lucas', 'Charlotte', 'Henry', 'Amelia',
  'Alexander', 'Harper', 'Michael', 'Evelyn', 'Ethan',
]

const lastNames = [
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller',
  'Davis', 'Rodriguez', 'Martinez', 'Wilson', 'Anderson', 'Taylor', 'Thomas',
  'Moore', 'Jackson', 'Martin', 'Lee', 'Perez', 'Thompson',
]

const randomFrom = (values: string[]) => values[Math.floor(Math.random() * values.length)]

const randomCompanyName = () =>
  companyNames[Math.floor(Math.random() * companyNames.length)]

interface CreateMerchantFormProps {
  onCreated: (details: TenantResponse) => void
  planKey?: string
}

interface FormState {
  companyName: string
  companyEmail: string
  companyPhone: string
  companyWebsiteUrl: string
  companyRegistrationNumber: string
  bankAccountName: string
  bankAccountRoutingNumber: string
  bankAccountNumber: string
  streetAddress: string
  suburb: string
  state: string
  postcode: string
  country: string
  natureOfBusiness: string
  organisationType: string
  contactFirstName: string
  contactLastName: string
  contactEmail: string
  contactPhone: string
  contactDob: string
  contactJobTitle: string
  contactOwnership: string
}

const contactFirstName = randomFrom(firstNames)
const contactLastName = randomFrom(lastNames)

const baseState: FormState = {
  companyName: randomCompanyName(),
  companyEmail: 'billing@example.com',
  companyPhone: '+61399999999',
  companyWebsiteUrl: 'https://example.com',
  companyRegistrationNumber: '51824753556',
  bankAccountName: randomCompanyName(),
  bankAccountRoutingNumber: '062000',
  bankAccountNumber: '12345678',
  streetAddress: '123 Collins Street',
  suburb: 'Melbourne',
  state: 'VIC',
  postcode: '3000',
  country: 'AU',
  natureOfBusiness: 'Conveyancing',
  organisationType: 'Company',
  contactFirstName,
  contactLastName,
  contactEmail: `${contactFirstName.toLowerCase()}.${contactLastName.toLowerCase()}@example.com`,
  contactPhone: '+61400000000',
  contactDob: '1985-06-15',
  contactJobTitle: 'Director',
  contactOwnership: '100',
}

// Lazy initializer so the timestamp suffix is stamped once per form mount, not on every render.
const createInitialState = (): FormState => ({
  ...baseState,
})

export const CreateMerchantForm: React.FC<CreateMerchantFormProps> = ({ onCreated, planKey }) => {
  const [form, setForm] = useState<FormState>(createInitialState)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { createManagedMerchant } = useTenantService()

  const handleChange = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!form.companyName.trim()) {
      setError('Company name is required')
      return
    }

    const payload: CreateManagedMerchantPayload = {
      planKey,
      companyName: form.companyName,
      companyEmail: form.companyEmail,
      companyPhone: form.companyPhone,
      companyWebsiteUrl: form.companyWebsiteUrl,
      companyRegistrationNumber: form.companyRegistrationNumber,
      bankAccountName: form.bankAccountName,
      bankAccountRoutingNumber: form.bankAccountRoutingNumber,
      bankAccountNumber: form.bankAccountNumber,
      streetAddress: form.streetAddress,
      suburb: form.suburb,
      state: form.state,
      postcode: form.postcode,
      country: form.country,
      natureOfBusiness: form.natureOfBusiness,
      organisationType: form.organisationType,
      contacts: [
        {
          isPrimaryContact: true,
          firstName: form.contactFirstName,
          lastName: form.contactLastName,
          email: form.contactEmail,
          phone: form.contactPhone,
          streetAddress: form.streetAddress,
          suburb: form.suburb,
          postcode: form.postcode,
          state: form.state,
          country: form.country,
          dob: form.contactDob,
          contactType: 'owner',
          ownership: Number(form.contactOwnership) || 0,
          jobTitle: form.contactJobTitle,
        },
      ],
    }

    setLoading(true)
    try {
      const details = await createManagedMerchant(payload)
      onCreated(details)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create managed merchant')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && <Alert type="error">{error}</Alert>}

      <div>
        <h3 className="font-medium text-gray-900 mb-3">Company Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Company Name" required value={form.companyName} onChange={handleChange('companyName')} />
          <Input label="Company Email" type="email" value={form.companyEmail} onChange={handleChange('companyEmail')} />
          <Input label="Company Phone" value={form.companyPhone} onChange={handleChange('companyPhone')} />
          <Input label="Company Website URL" value={form.companyWebsiteUrl} onChange={handleChange('companyWebsiteUrl')} />
          <Input label="Company Registration Number (ABN)" value={form.companyRegistrationNumber} onChange={handleChange('companyRegistrationNumber')} />
          <Input label="Nature of Business" value={form.natureOfBusiness} onChange={handleChange('natureOfBusiness')} />
          <Input label="Organisation Type" value={form.organisationType} onChange={handleChange('organisationType')} />
        </div>
      </div>

      <div>
        <h3 className="font-medium text-gray-900 mb-3">Business Address</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Street Address" value={form.streetAddress} onChange={handleChange('streetAddress')} />
          <Input label="Suburb" value={form.suburb} onChange={handleChange('suburb')} />
          <Input label="State" value={form.state} onChange={handleChange('state')} />
          <Input label="Postcode" value={form.postcode} onChange={handleChange('postcode')} />
          <Input label="Country" value={form.country} onChange={handleChange('country')} />
        </div>
      </div>

      <div>
        <h3 className="font-medium text-gray-900 mb-3">Bank Account</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Account Name" value={form.bankAccountName} onChange={handleChange('bankAccountName')} />
          <Input label="BSB" value={form.bankAccountRoutingNumber} onChange={handleChange('bankAccountRoutingNumber')} />
          <Input label="Account Number" value={form.bankAccountNumber} onChange={handleChange('bankAccountNumber')} />
        </div>
      </div>

      <div>
        <h3 className="font-medium text-gray-900 mb-3">Primary Contact</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="First Name" value={form.contactFirstName} onChange={handleChange('contactFirstName')} />
          <Input label="Last Name" value={form.contactLastName} onChange={handleChange('contactLastName')} />
          <Input label="Email" type="email" value={form.contactEmail} onChange={handleChange('contactEmail')} />
          <Input label="Phone" value={form.contactPhone} onChange={handleChange('contactPhone')} />
          <Input label="Date of Birth" type="date" value={form.contactDob} onChange={handleChange('contactDob')} />
          <Input label="Job Title" value={form.contactJobTitle} onChange={handleChange('contactJobTitle')} />
          <Input label="Ownership %" type="number" min="0" max="100" value={form.contactOwnership} onChange={handleChange('contactOwnership')} />
        </div>
      </div>

      <Button type="submit" disabled={loading}>
        {loading ? 'Signing up...' : 'Sign Up'}
      </Button>
    </form>
  )
}
